import React, { useEffect, useMemo } from "react";

import { Crossfade, EmptyState, FluidContainerHeight, Skeleton } from "@performyard/py-components";

import { SubsectionUtilGroup } from "../../components/SubsectionUtilGroup";
import RespondentsList from "./components/RespondentsList";
import SurveyResponse from "./components/SurveyResponse";

import { useGetSurveyParticipantsInfinite } from "../../../../../../../../api/hooks/defs/surveys";
import useSearchQueryParams from "../../../../../../../../hooks/useSearchQueryParams";

import { PARTICIPANT_PAGE_SIZE, searchParamsSchema } from "./utils";

type ResponsesProps = {
	surveyId: string;
};

const Responses = ({ surveyId }: ResponsesProps) => {
	const {
		queryParams: searchParams = { search: "", sort: "asc", employeeId: undefined },
		handleQueryChange: setSearchParams,
	} = useSearchQueryParams({
		schema: searchParamsSchema,
	});

	// Fetch first page to check if any responses exist and to auto-select first participant
	const { data, isLoading } = useGetSurveyParticipantsInfinite(surveyId, {
		search: "",
		sort: searchParams?.sort ?? "asc",
		limit: PARTICIPANT_PAGE_SIZE,
	});

	const firstPageParticipants = data?.pages[0]?.participants ?? [];
	const total = data?.pages[0]?.total ?? 0;
	const hasNoResponses = !isLoading && total === 0;

	// Auto-select first participant on initial load if no employeeId is set
	const firstParticipantId = firstPageParticipants[0]?._id;
	useEffect(() => {
		if (!searchParams?.employeeId && firstParticipantId) {
			setSearchParams({ ...searchParams, employeeId: firstParticipantId });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [firstParticipantId]);

	const view = useMemo<React.ReactNode>(() => {
		if (isLoading) {
			return (
				<div className="grid h-[760px] grid-cols-[300px_1fr] gap-0 overflow-hidden">
					<div className="flex flex-col overflow-hidden">
						<RespondentsList surveyId={surveyId} />
					</div>
					<div className="w-full border-l border-solid border-neutral-2 bg-neutral-1 px-4 py-10">
						<div className="mx-auto w-full max-w-[960px] p-10">
							<Skeleton className="mb-4 h-8 w-64" />
							<Skeleton className="mb-2 h-4 w-full" />
							<Skeleton className="h-4 w-3/4" />
						</div>
					</div>
				</div>
			);
		}
		if (hasNoResponses) {
			return (
				<div className="flex h-auto flex-col items-center justify-center py-32">
					<EmptyState variant="illustration" option={2} label="No Survey Responses" />
				</div>
			);
		}
		return (
			<div className="grid h-[760px] grid-cols-[300px_1fr] gap-0 overflow-hidden">
				<RespondentsList surveyId={surveyId} />
				<div className="max-h-full overflow-y-scroll border-l border-solid border-neutral-2 bg-neutral-1 px-4">
					<div className="mx-auto my-10 max-w-[880px]">
						<SurveyResponse surveyId={surveyId} participantId={searchParams?.employeeId} />
					</div>
				</div>
			</div>
		);
	}, [isLoading, hasNoResponses, searchParams?.employeeId]);

	return (
		<div className="mt-4 flex flex-col gap-4">
			<SubsectionUtilGroup surveyId={surveyId} showSummaryCSV />
			<div
				data-empty={hasNoResponses}
				className="group overflow-hidden rounded-2 border border-solid border-neutral-2"
			>
				<FluidContainerHeight>
					<Crossfade viewKey={isLoading ? "loading" : hasNoResponses ? "no-responses" : "responses"}>
						{view}
					</Crossfade>
				</FluidContainerHeight>
			</div>
		</div>
	);
};

export default Responses;
